import React, { useEffect, useState } from "react";
import { useTitle } from "../../context/TitleContext";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  loginUser,
  sendVerifyCodeFunc,
  clearState,
} from "../../redux/Slices/authSlice";
import { toast } from "react-toastify";
import { hasLocalAuthSession, getStoredUser } from "../../utils/authStorage";
import {
  canAccessPath,
  getPermissionStateFromUser,
  ROUTE_PERMISSION_RULES,
} from "../../utils/permissions";

function getPostLoginPath(userData) {
  const { enforce, keys } = getPermissionStateFromUser(
    userData || getStoredUser()
  );
  if (!enforce || canAccessPath("/", keys, { enforce: true })) {
    return "/";
  }
  const candidates = ROUTE_PERMISSION_RULES.map((r) => r.path).filter(
    (p) => p !== "/"
  );
  for (const path of candidates) {
    if (canAccessPath(path, keys, { enforce: true })) return path;
  }
  return "/unauthorized";
}

const Login = () => {
  const { t, i18n } = useTranslation("global");
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, loading, user } = useSelector((state) => state.auth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [email_to_verify, setEmail_to_verify] = useState("");

  useEffect(() => {
    setTitle(t("sign.login"));
    if (hasLocalAuthSession() || user) {
      const data = user?.data || user || getStoredUser();
      navigate(getPostLoginPath(data), { replace: true });
    }
  }, [setTitle, t, i18n.language, user, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error(t("sign.fillFields"));
      return;
    }

    dispatch(loginUser({ email, password }))
      .unwrap()
      .then((res) => {
        toast.success(t("sign.loginSuccess"));
        navigate(getPostLoginPath(res?.data), { replace: true });
      })
      .catch((err) => {
        toast.error(t("sign.invalid_credentials") || err?.message);
      });
  };

  const sendVerifyCode = (e) => {
    e.preventDefault();
    if (!email_to_verify) {
      toast.error(t("sign.fillFields"));
      return;
    }
    dispatch(sendVerifyCodeFunc(email_to_verify))
      .unwrap()
      .then(() => {
        toast.success(t("email_verified_successfully"));
        setTimeout(() => {
          document.body.classList.remove("modal-open");
          document
            .querySelectorAll(".modal-backdrop")
            .forEach((el) => el.remove());
          const modalEl = document.getElementById("forgotPassword");
          if (modalEl) modalEl.classList.remove("show");

          navigate("/verify_code");
          dispatch(clearState());
        }, 1000);
      })
      .catch((err) => {
        toast.error(t("failed_to_verify_email") || err?.message);
        dispatch(clearState());
      });
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    // Update document direction and language
    document.documentElement.lang = lng;
    document.documentElement.dir = lng === "ar" ? "rtl" : "ltr";
  };
  return (
    <main className=" d-flex justify-content-center align-items-center min-vh-100 position-relative">
      {/* Language Toggle */}
      <div
        className="position-absolute"
        style={{
          top: "20px",
          right: i18n.language === "ar" ? "auto" : "20px",
          left: i18n.language === "ar" ? "20px" : "auto",
          zIndex: 1000,
        }}
      >
        <div
          className="btn-group"
          role="group"
          aria-label="Language selector"
          style={{
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 2px 12px rgba(0, 106, 83, 0.2)",
            backgroundColor: "white",
          }}
        >
          <button
            type="button"
            className={`btn ${
              i18n.language === "ar"
                ? "text-white"
                : "btn-light"
            }`}
            onClick={() => changeLanguage("ar")}
            style={{
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              transition: "all 0.3s ease",
              backgroundColor: i18n.language === "ar" ? "var(--green-color)" : "transparent",
              color: i18n.language === "ar" ? "white" : "#666",
            }}
            onMouseEnter={(e) => {
              if (i18n.language !== "ar") {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
              }
            }}
            onMouseLeave={(e) => {
              if (i18n.language !== "ar") {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
          >
            <i className="bi bi-translate me-1"></i>
            العربية
          </button>
          <button
            type="button"
            className={`btn ${
              i18n.language === "en"
                ? "text-white"
                : "btn-light"
            }`}
            onClick={() => changeLanguage("en")}
            style={{
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              transition: "all 0.3s ease",
              backgroundColor: i18n.language === "en" ? "var(--green-color)" : "transparent",
              color: i18n.language === "en" ? "white" : "#666",
            }}
            onMouseEnter={(e) => {
              if (i18n.language !== "en") {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
              }
            }}
            onMouseLeave={(e) => {
              if (i18n.language !== "en") {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
          >
            <i className="bi bi-translate me-1"></i>
            English
          </button>
        </div>
      </div>

      <div className="container ">

        <form
          className="table_form form-style my-3 py-3"
          onSubmit={handleSubmit}
        >
          <div className="form-div">
            <div className="text-center p-3 mx-auto rounded-3 my-3">
              <Link to="/">
                <picture>
                  <source srcSet="/img/fav.webp" type="image/webp" />
                  <img
                    src="/img/fav.png"
                    alt="Tripway"
                    width={180}
                    height={60}
                    fetchPriority="high"
                    decoding="async"
                    style={{ width: "auto", maxHeight: "60px", height: "60px" }}
                  />
                </picture>
              </Link>
            </div>
            <h1 className="h5">{t("sign.login")}</h1>
            <label>{t("sign.email")}</label>
            <input
              type="email"
              name="email"
              placeholder={t("sign.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label>{t("sign.password")}</label>
            <input
              type="password"
              name="password"
              placeholder={t("sign.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <div className="text-center">
              <button
                type="button"
                data-bs-toggle="modal"
                data-bs-target="#forgotPassword"
                className="btn text-secondary px-0 py-3 m-0"
              >
                {t("sign.forgotPassword")}
              </button>
            </div>
            <button
              type="submit"
              className="d-block w-100"
              disabled={isLoading}
            >
              {isLoading ? t("sign.loading") : t("sign.login")}
            </button>
          </div>
        </form>
      </div>
      <div
        className="modal fade"
        id="forgotPassword"
        tabIndex="-1"
        aria-labelledby="forgotPasswordLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow-lg">
            <div
              className="modal-header border-0 pb-0"
              style={{
                background: "linear-gradient(135deg, var(--green-color) 0%, #28a745 100%)",
                padding: "1rem",
                borderRadius: "0.375rem 0.375rem 0 0",
              }}
            >
              <div className="d-flex align-items-center w-100">
                <div className="d-flex align-items-center text-white w-100">
                  <i
                    className="bi bi-envelope-fill"
                    style={{ fontSize: "1.5rem", margin: "0.5rem " }}
                  ></i>
                  <h5 className="modal-title fw-bold mb-0 text-white">
                    {t("send_email_to_verify")}
                  </h5>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white ms-auto"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  style={{ opacity: 1 }}
                ></button>
              </div>
            </div>
            <div className="modal-body px-4 py-4">
              <form onSubmit={sendVerifyCode} className="form-style">
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-2 d-flex align-items-center">
                    <i className="bi bi-envelope mx-2" style={{ color: "var(--green-color)" }}></i>
                    {t("sign.email")}
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="example@gmail.com"
                    required
                    value={email_to_verify}
                    onChange={(e) => setEmail_to_verify(e.target.value)}
                    style={{
                      padding: "0.75rem",
                      fontSize: "1rem",
                      border: "1px solid #dee2e6",
                      borderRadius: "0.375rem",
                    }}
                  />
                </div>
                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn text-white"
                    style={{
                      backgroundColor: "var(--green-color)",
                      padding: "0.5rem",
                      fontSize: "1rem",
                      fontWeight: "600",
                      borderRadius: "0.375rem",
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t("labels.loading")}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-send me-2"></i>
                        {t("btns.send")}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                    style={{
                      padding: "0.5rem",
                      fontSize: "1rem",
                      borderRadius: "0.375rem",
                    }}
                  >
                    {t("btns.cancel")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login;
